import { usePage } from '@inertiajs/react'
import { MessagesSquare, Send, X } from 'lucide-react'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import type { SharedProps } from '@/types'
import { useI18n } from '../i18n'
import { OPEN_CHAT_EVENT } from './chat-bus'

/**
 * Live-chat affordance for every marketing page (shell slot from 0002).
 *
 * Production uses **Chatwoot** (the customer-service tool in use). The
 * public-safe config (instance base URL + website token) is resolved
 * server-side and shared on the page as the `chatwoot` Inertia prop — the admin
 * API token never reaches the browser (see Marketing::ChatwootWidget). When
 * that prop is present AND the visitor has accepted cookies, the Chatwoot SDK is
 * loaded async and this in-app panel steps aside. Until chat is configured we
 * render the mockup's demo panel 1:1 so the page is complete and the affordance
 * works; the Contact page is always the non-JS fallback.
 */
type Msg = { id: number; who: string; text: string; mine: boolean }
const AGENT = 'Erin · Geo4Logistics'

let MSG_ID = 0
const mkMsg = (who: string, text: string, mine: boolean): Msg => ({ id: MSG_ID++, who, text, mine })

export default function ChatWidget() {
  const { t, locale } = useI18n()
  const chatwoot = usePage<SharedProps>().props.chatwoot
  const [open, setOpen] = useState(false)
  const [badge, setBadge] = useState(true)
  const [quickVisible, setQuickVisible] = useState(true)
  const [draft, setDraft] = useState('')
  const [messages, setMessages] = useState<Msg[]>([])
  const logRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // If Chatwoot is configured we defer to it instead of the demo panel.
  const chatwootEnabled = Boolean(chatwoot)

  // Open on the shared "start a chat" event (Contact / Help links).
  useEffect(() => {
    function onOpen() {
      if (chatwootEnabled) {
        ;(window as unknown as { $chatwoot?: { toggle: (s: 'open') => void } }).$chatwoot?.toggle(
          'open',
        )
      } else {
        setOpen(true)
      }
    }
    window.addEventListener(OPEN_CHAT_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpen)
  }, [chatwootEnabled])

  // Keep the greeting in sync with the active locale while it's the only message.
  useEffect(() => {
    setMessages((prev) =>
      prev.length === 0 || (prev.length === 1 && !prev[0].mine)
        ? [mkMsg(AGENT, t('chat.hello'), false)]
        : prev,
    )
  }, [t])

  // Scroll the log to the latest message whenever it changes.
  useEffect(() => {
    const el = logRef.current
    if (el && messages.length) el.scrollTop = el.scrollHeight
  }, [messages])

  // On open: clear the unread badge and focus the composer.
  useEffect(() => {
    if (!open) return
    setBadge(false)
    const id = window.setTimeout(() => inputRef.current?.focus(), 120)
    return () => window.clearTimeout(id)
  }, [open])

  // Chatwoot loader — async, gated on the shared config prop + cookie consent.
  useEffect(() => {
    if (!chatwoot) return
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem('g4-consent') !== 'accepted') return
    const w = window as unknown as {
      chatwootSettings?: Record<string, unknown>
      chatwootSDK?: { run: (o: { websiteToken: string; baseUrl: string }) => void }
    }
    w.chatwootSettings = { position: 'right', type: 'expanded_bubble', launcherTitle: 'Chat' }
    const s = document.createElement('script')
    s.src = `${chatwoot.base_url}/packs/js/sdk.js`
    s.defer = true
    s.async = true
    s.onload = () =>
      w.chatwootSDK?.run({
        websiteToken: chatwoot.website_token,
        baseUrl: chatwoot.base_url,
      })
    document.body.appendChild(s)
  }, [chatwoot])

  // Map our locale → Chatwoot's locale codes when the language toggle changes.
  useEffect(() => {
    if (!chatwootEnabled) return
    const map: Record<string, string> = { en: 'en', 'zh-Hant': 'zh_TW', 'zh-Hans': 'zh_CN' }
    ;(window as unknown as { $chatwoot?: { setLocale: (l: string) => void } }).$chatwoot?.setLocale(
      map[locale],
    )
  }, [locale, chatwootEnabled])

  if (chatwootEnabled) return null // Chatwoot renders its own launcher.

  function reply() {
    window.setTimeout(() => {
      setMessages((m) => [...m, mkMsg(AGENT, t('chat.reply'), false)])
    }, 700)
  }
  function send(e: FormEvent) {
    e.preventDefault()
    const v = draft.trim()
    if (!v) return
    setMessages((m) => [...m, mkMsg('You', v, true)])
    setDraft('')
    reply()
  }
  function quick(text: string) {
    setMessages((m) => [...m, mkMsg('You', text, true)])
    setQuickVisible(false)
    reply()
  }

  return (
    <>
      <button
        type="button"
        className="chat-fab"
        aria-label={t('a11y.openChat')}
        onClick={() => setOpen((v) => !v)}
      >
        <MessagesSquare />
        {badge && <span className="badge-dot">1</span>}
      </button>

      <div
        className={open ? 'chat-panel on' : 'chat-panel'}
        role="dialog"
        aria-label={t('chat.title')}
      >
        <div className="chat-head">
          <div className="row">
            <div className="agents">
              <span className="av">EW</span>
              <span className="av">DF</span>
              <span className="av">CW</span>
            </div>
            <button
              type="button"
              className="chat-x"
              aria-label={t('a11y.closeChat')}
              onClick={() => setOpen(false)}
            >
              <X />
            </button>
          </div>
          <h4>{t('chat.title')}</h4>
          <p>
            <span className="online" />
            <span>{t('chat.status')}</span>
          </p>
        </div>

        <div className="chat-log" ref={logRef}>
          {messages.map((m) => (
            <div className={m.mine ? 'cmsg me' : 'cmsg them'} key={m.id}>
              {!m.mine && <div className="w">{m.who}</div>}
              <div className="b">{m.text}</div>
            </div>
          ))}
        </div>

        {quickVisible && (
          <div className="chat-quick">
            <button type="button" onClick={() => quick(t('chat.q1'))}>
              {t('chat.q1')}
            </button>
            <button type="button" onClick={() => quick(t('chat.q2'))}>
              {t('chat.q2')}
            </button>
            <button type="button" onClick={() => quick(t('chat.q3'))}>
              {t('chat.q3')}
            </button>
          </div>
        )}

        <form className="chat-compose" onSubmit={send}>
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('chat.placeholder')}
            aria-label={t('chat.placeholder')}
            autoComplete="off"
          />
          <button className="chat-send" type="submit" aria-label={t('a11y.send')}>
            <Send />
          </button>
        </form>
        <div className="chat-foot">{t('chat.foot')}</div>
      </div>
    </>
  )
}
